import React from 'react'
import { css } from '@emotion/css'
import { VrModulePanel } from '../layout/vr-module-containers'
import {
  VrCodeSourceRatingData,
  VrModuleCodeSourceRatingData,
  VrCodeSourceRatingDataValue
} from '.'
import { VrThermometerHeader } from '../vr-thermometer-header'
import { GradeSegmentedThermometer } from '../../../grades/grade-thermometer'
import { VrModuleTitle, VrModuleFlexDirection } from '../layout'
import {
  LoadingMaskRectangle,
  LoadingMaskGradeBadgeSmall
} from '../../../../legacy-design-system/loading-masks'
import { VrModuleTitleLoadingView } from '../layout/vr-module-title'
import { SegmentedThermometerLoadingView } from '../../../../legacy-design-system/thermometer'
import { GradeBadge } from '../../../grades/grade-badge'
import { IntlShape } from 'react-intl'

export interface VrModuleCodeSourceRatingProps {
  readonly isDataLoading: boolean
  readonly data?: VrModuleCodeSourceRatingData
  readonly intl: {
    readonly formatMessage: IntlShape['formatMessage']
  }
}

const Wrapper = ({
  children,
  testId
}: {
  readonly children: React.ReactNode
  readonly testId?: string
}) => (
  <VrModulePanel hasBorder={false} order={2} minHeight={260} testId={testId}>
    <div
      className={css`
        display: flex;
        flex-direction: column;
        width: 100%;
      `}
    >
      {children}
    </div>
  </VrModulePanel>
)

const InnerWrapper = ({
  flexDirection,
  children
}: {
  readonly flexDirection: VrModuleFlexDirection
  readonly children: React.ReactNode
}) => {
  return (
    <div
      className={css`
        display: flex;
        flex-direction: ${flexDirection};
        width: 100%;
      `}
    >
      {children}
    </div>
  )
}

const testIdPrefix = 'value-reporting-code-source-ratings-module'

export function VrModuleCodeSourceRating({
  isDataLoading,
  data,
  intl
}: VrModuleCodeSourceRatingProps) {
  const flexDirection: VrModuleFlexDirection = 'column'

  if (isDataLoading) {
    return (
      <Wrapper>
        <LoadingView flexDirection={flexDirection} />
      </Wrapper>
    )
  }

  if (!data) {
    throw new Error('data is undefined')
  }

  return (
    <Wrapper testId={testIdPrefix}>
      <VrModuleTitle id="title_valueReporting_ratingByCodeSource" />
      <InnerWrapper flexDirection={flexDirection}>
        <DataView
          dataTestId={`${testIdPrefix}-custom`}
          e2eTestId={`${testIdPrefix}-custom`}
          titleLocaleId="title_valueReporting_customCode"
          getSegmentLabel={({ count }) =>
            intl.formatMessage(
              {
                id: 'plural_valueReporting_application'
              },
              {
                count
              }
            )
          }
          data={data.customCode}
        />
        <DataView
          dataTestId={`${testIdPrefix}-library`}
          e2eTestId={`${testIdPrefix}-library`}
          titleLocaleId="title_libraries"
          getSegmentLabel={({ count }) =>
            intl.formatMessage(
              {
                id: 'plural_valueReporting_library'
              },
              {
                count
              }
            )
          }
          data={data.libraries}
        />
      </InnerWrapper>
    </Wrapper>
  )
}

function LoadingView({
  flexDirection
}: {
  readonly flexDirection: VrModuleFlexDirection
}) {
  return (
    <span data-testid="vr-module-code-source-rating-loading-view">
      <VrModuleTitleLoadingView />
      <InnerWrapper flexDirection={flexDirection}>
        <DataLoadingView />
        <DataLoadingView />
      </InnerWrapper>
    </span>
  )
}

interface DataViewProps {
  readonly titleLocaleId: string
  readonly data: VrCodeSourceRatingData
  dataTestId?: string
  readonly e2eTestId: string
  readonly getSegmentLabel: (segmentData: VrCodeSourceRatingDataValue) => string
}

const DataViewWrapper = ({ renderHeader, renderGrade, renderThermometer }) => {
  return (
    <div
      className={css`
        margin-bottom: 20px;
      `}
    >
      {renderHeader()}
      <div
        className={css`
          display: flex;
          align-items: center;
        `}
      >
        <span
          className={css`
            margin-right: 10px;
          `}
        >
          {renderGrade()}
        </span>
        {renderThermometer()}
      </div>
    </div>
  )
}

function DataLoadingView() {
  return (
    <DataViewWrapper
      renderHeader={() => (
        <LoadingMaskRectangle
          width={100}
          height={19}
          className={css`
            margin-bottom: 10px;
          `}
        />
      )}
      renderGrade={() => <LoadingMaskGradeBadgeSmall />}
      renderThermometer={() => <SegmentedThermometerLoadingView />}
    />
  )
}

function DataView({
  titleLocaleId,
  data: { total, grade, values },
  dataTestId,
  e2eTestId,
  getSegmentLabel
}: DataViewProps) {
  return (
    <DataViewWrapper
      renderHeader={() => (
        <VrThermometerHeader
          i18nID={titleLocaleId}
          count={total}
          e2eTestId={`${e2eTestId}-header`}
        />
      )}
      renderGrade={() => (
        <GradeBadge
          value={grade}
          dataTestId={`${dataTestId}-grade`}
          e2eTestId={`${e2eTestId}-grade`}
        />
      )}
      renderThermometer={() => {
        return (
          <GradeSegmentedThermometer
            data={values
              .map(({ value, count }) => ({
                grade: value,
                value: count,
                label: getSegmentLabel({ value, count })
              }))
              .reverse()}
            e2eTestId={`${e2eTestId}-thermometer`}
          />
        )
      }}
    />
  )
}
